import BaseTableNew from 'components/atoms/base-table/new';
import { ACTIVITY_PER_PAGE } from 'infrastructure/hooks/rpm';
import { scrollToTopAsync } from 'infrastructure/functions';
import { type FC, type Dispatch, type SetStateAction } from 'react';
import { useAppDispatch } from 'store';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import { useTranslation } from 'react-i18next';
import SearchIcon from 'components/atoms/icons/search.svg?react';
import useUser from 'utils/useUser';
import classNames from 'classnames';
import { ELASTIC_SEARCH_MAX_ITEMS } from 'constants/count';
import { safeClamp } from 'utils/helpers/safeClamp';

import s from './styles.module.scss';
import { useActivityColumns } from './columns';
import { usePatientActivityColumns } from './patient-columns';

import type { TView } from './types';
import type { ActivityStatus, ModuleType } from 'infrastructure/enums';
import type { SortState } from 'infrastructure/types';
import type {
  TRpmDashboardActivityQueryKey,
  TRpmPatientActivityQueryKey,
} from 'infrastructure/hooks/rpm';
import type { IRpmPatientActivity } from 'infrastructure/interfaces/i-rpm-patient-activity';

interface ActivityTableProps {
  loading: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  onSort: (field: string, sort: SortState) => void;
  itemsQueryKey: TRpmDashboardActivityQueryKey | TRpmPatientActivityQueryKey;
  showReviewCol: boolean;
  hideReviewCheckbox?: boolean;
  activity?: IRpmPatientActivity;
  view: TView;
  pageSize?: number;
  setPerPage?: Dispatch<SetStateAction<number>>;
  showSizeChanger?: boolean;
  tableId?: string;
  tableTypePrefix?: 'a' | 'u';
  module: ModuleType;
  type?: ActivityStatus;
}

const ActivityTable: FC<ActivityTableProps> = ({
  activity,
  loading,
  page,
  setPage,
  setPerPage,
  onSort,
  itemsQueryKey,
  showReviewCol,
  hideReviewCheckbox,
  view,
  pageSize = ACTIVITY_PER_PAGE,
  showSizeChanger,
  tableId = 'activity',
  tableTypePrefix,
  module,
  type,
}) => {
  const { isPatient } = useUser();
  const activityColumns = useActivityColumns({
    showReviewCol,
    view,
    getActivityQueryKey: itemsQueryKey,
    tableTypePrefix,
    module,
    isShowAssignedToggle: showReviewCol,
    hideReviewCheckbox,
    type,
  });
  const patientActivityColumns = usePatientActivityColumns({
    getActivityQueryKey: itemsQueryKey,
    view,
  });

  const {
    activityActions: { resetActions },
  } = useAppDispatch();
  const { t } = useTranslation();

  return (
    <>
      <BaseTableNew
        dataCy="activity-table"
        loading={loading}
        rowKey={(record) => `${record.deviceId}-${record.id}`}
        columns={isPatient ? patientActivityColumns : activityColumns}
        items={activity?.data ?? []}
        bordered
        pagination={{
          current: page,
          pageSize,
          total: safeClamp(activity?.count, 0, ELASTIC_SEARCH_MAX_ITEMS),
          position: ['bottomCenter'],
          showSizeChanger,
        }}
        onChangePage={(newPage, newPageSize) => {
          resetActions();
          scrollToTopAsync(tableId, 70).then(() => {
            setPage(newPage);
            if (pageSize !== newPageSize) {
              setPage(1);
              setPerPage?.(newPageSize);
            }
          });
        }}
        emptyText={
          <EmptyTablePlaceholder
            text={t('labels.noReadingsFound')}
            icon={<SearchIcon width={100} height={100} />}
            column
          />
        }
        onSort={onSort}
        rowClassName={classNames(s['table-row'], {
          [s['is-patient']]: isPatient,
        })}
        rowHoverable={false}
      />
    </>
  );
};
export default ActivityTable;
